// AR / AP aging — better than QBO: every overdue row has a one-click
// "Send WhatsApp reminder" button so you can chase customers without
// leaving the page.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Mail, Eye } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatDate, formatMoney, todayISO } from '../lib/format';

type Side = 'receivable' | 'payable';
interface AgingRow {
  doc_id: string;
  number: string;
  contact_id: string;
  contact_name: string;
  contact_phone: string | null;
  contact_email: string | null;
  issue_date: string;
  due_date: string;
  total: number;
  amount_paid: number;
  outstanding: number;
  days_overdue: number;
  bucket: 'current' | 'd30' | 'd60' | 'd90' | 'd90plus';
  public_token?: string;
  currency: string;
}

const BUCKET_LABEL: Record<AgingRow['bucket'], string> = {
  current: 'Current',
  d30: '1–30 days',
  d60: '31–60 days',
  d90: '61–90 days',
  d90plus: 'Over 90',
};
const BUCKET_COLOR: Record<AgingRow['bucket'], string> = {
  current: 'text-emerald',
  d30: 'text-gold',
  d60: 'text-gold',
  d90: 'text-rust',
  d90plus: 'text-rust',
};

function bucketFor(days: number): AgingRow['bucket'] {
  if (days <= 0) return 'current';
  if (days <= 30) return 'd30';
  if (days <= 60) return 'd60';
  if (days <= 90) return 'd90';
  return 'd90plus';
}

export default function ReportAging() {
  const { currentOrg } = useOrg();
  const [side, setSide] = useState<Side>('receivable');
  const [rows, setRows] = useState<AgingRow[]>([]);
  const today = todayISO();

  useEffect(() => {
    if (!currentOrg) return;
    (async () => {
      if (side === 'receivable') {
        const { data } = await supabase
          .from('dodobooks_invoices')
          .select('id, number, contact_id, issue_date, due_date, total, amount_paid, currency, public_token, kind, status, dodobooks_contacts(name, phone, email)')
          .eq('org_id', currentOrg.id)
          .in('status', ['sent', 'overdue', 'paid'])
          .eq('kind', 'invoice');
        setRows(((data ?? []) as any[])
          .map((r) => {
            const outstanding = Number(r.total) - Number(r.amount_paid);
            const days = Math.floor((new Date(today).getTime() - new Date(r.due_date).getTime()) / 86400000);
            return {
              doc_id: r.id, number: r.number, contact_id: r.contact_id,
              contact_name: r.dodobooks_contacts?.name ?? '—',
              contact_phone: r.dodobooks_contacts?.phone ?? null,
              contact_email: r.dodobooks_contacts?.email ?? null,
              issue_date: r.issue_date, due_date: r.due_date,
              total: Number(r.total), amount_paid: Number(r.amount_paid), outstanding,
              days_overdue: Math.max(0, days), bucket: bucketFor(days),
              public_token: r.public_token, currency: r.currency,
            } as AgingRow;
          })
          .filter((r) => r.outstanding > 0.005)
          .sort((a, b) => b.days_overdue - a.days_overdue || b.outstanding - a.outstanding));
      } else {
        const { data } = await supabase
          .from('dodobooks_bills')
          .select('id, supplier_invoice_number, internal_ref, contact_id, issue_date, due_date, total, amount_paid, currency, kind, status, dodobooks_contacts(name, phone, email)')
          .eq('org_id', currentOrg.id)
          .in('status', ['approved', 'paid'])
          .eq('kind', 'bill');
        setRows(((data ?? []) as any[])
          .map((r) => {
            const outstanding = Number(r.total) - Number(r.amount_paid);
            const days = Math.floor((new Date(today).getTime() - new Date(r.due_date).getTime()) / 86400000);
            return {
              doc_id: r.id, number: r.supplier_invoice_number || r.internal_ref || r.id.slice(0, 8),
              contact_id: r.contact_id,
              contact_name: r.dodobooks_contacts?.name ?? '—',
              contact_phone: r.dodobooks_contacts?.phone ?? null,
              contact_email: r.dodobooks_contacts?.email ?? null,
              issue_date: r.issue_date, due_date: r.due_date,
              total: Number(r.total), amount_paid: Number(r.amount_paid), outstanding,
              days_overdue: Math.max(0, days), bucket: bucketFor(days),
              currency: r.currency,
            } as AgingRow;
          })
          .filter((r) => r.outstanding > 0.005)
          .sort((a, b) => b.days_overdue - a.days_overdue || b.outstanding - a.outstanding));
      }
    })();
  }, [currentOrg, side, today]);

  const totals = useMemo(() => {
    const t = { current: 0, d30: 0, d60: 0, d90: 0, d90plus: 0, all: 0 } as Record<string, number>;
    for (const r of rows) { t[r.bucket] += r.outstanding; t.all += r.outstanding; }
    return t;
  }, [rows]);

  const grouped = useMemo(() => {
    const out: Record<string, { contact_id: string; contact_name: string; phone: string | null; email: string | null; rows: AgingRow[]; total: number }> = {};
    for (const r of rows) {
      out[r.contact_id] = out[r.contact_id] ?? { contact_id: r.contact_id, contact_name: r.contact_name, phone: r.contact_phone, email: r.contact_email, rows: [], total: 0 };
      out[r.contact_id].rows.push(r);
      out[r.contact_id].total += r.outstanding;
    }
    return Object.values(out).sort((a, b) => b.total - a.total);
  }, [rows]);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="h-page">{side === 'receivable' ? 'Money owed to you' : 'You owe suppliers'}</h1>
          <p className="text-text-dim text-sm mt-1">
            {side === 'receivable'
              ? 'Outstanding invoices, oldest first. One tap reminds the customer.'
              : 'Outstanding bills, oldest first.'}
          </p>
        </div>
        <div className="flex bg-surface border border-line rounded-md overflow-hidden">
          {(['receivable', 'payable'] as const).map((s) => (
            <button key={s} type="button" onClick={() => setSide(s)}
              className={'px-4 py-1.5 text-xs font-mono uppercase tracking-wider ' + (side === s ? 'bg-emerald/10 text-emerald' : 'text-text-mute hover:text-text')}>
              {s === 'receivable' ? 'AR' : 'AP'}
            </button>
          ))}
        </div>
      </header>

      {/* Bucket summary */}
      <div className="grid grid-cols-5 gap-3">
        {(['current', 'd30', 'd60', 'd90', 'd90plus'] as const).map((b) => (
          <div key={b} className="card">
            <div className="text-[10px] font-mono uppercase tracking-widest text-text-mute mb-1">{BUCKET_LABEL[b]}</div>
            <div className={'font-serif font-semibold text-xl num ' + BUCKET_COLOR[b]}>{formatMoney(totals[b])}</div>
          </div>
        ))}
      </div>

      {/* Per-contact rollup */}
      {grouped.length === 0 ? (
        <div className="card text-center py-12 text-text-mute text-sm">
          Nothing outstanding — {side === 'receivable' ? 'all customers are paid up' : 'all bills are settled'}.
        </div>
      ) : grouped.map((g) => (
        <div key={g.contact_id} className="card p-0 overflow-hidden">
          <div className="px-5 py-3 border-b border-line flex items-center justify-between gap-3 bg-surface-2">
            <div>
              <Link to={side === 'receivable' ? `/contacts/${g.contact_id}/statement` : '#'} className="font-semibold hover:text-emerald">
                {g.contact_name}
              </Link>
              <span className="ml-3 text-xs text-text-mute font-mono">{g.rows.length} doc{g.rows.length === 1 ? '' : 's'}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="num text-lg font-semibold text-gold">{formatMoney(g.total)}</span>
              {side === 'receivable' && g.phone && (
                <a
                  href={`https://wa.me/${g.phone.replace(/[^\d]/g, '')}?text=${encodeURIComponent(`Hi ${g.contact_name}, just a friendly reminder — your account currently shows ${formatMoney(g.total)} outstanding. Could you let me know when payment is on the way? Thanks.`)}`}
                  target="_blank" rel="noopener" className="btn btn-primary btn-sm" title="Send WhatsApp reminder">
                  <MessageCircle size={12} /> Remind
                </a>
              )}
              {side === 'receivable' && g.email && !g.phone && (
                <a
                  href={`mailto:${g.email}?subject=${encodeURIComponent('Friendly payment reminder')}&body=${encodeURIComponent(`Hi ${g.contact_name},\n\nJust a friendly reminder — your account currently shows ${formatMoney(g.total)} outstanding.\n\nThanks!`)}`}
                  className="btn btn-secondary btn-sm" title="Email reminder">
                  <Mail size={12} /> Remind
                </a>
              )}
              {side === 'receivable' && (
                <Link to={`/contacts/${g.contact_id}/statement`} className="btn btn-ghost btn-sm" title="View statement">
                  <Eye size={12} />
                </Link>
              )}
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th className="px-5">Doc</th>
                <th>Issued</th>
                <th>Due</th>
                <th>Days</th>
                <th>Bucket</th>
                <th className="text-right pr-5">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {g.rows.map((r) => (
                <tr key={r.doc_id}>
                  <td className="px-5"><Link to={side === 'receivable' ? '/invoices/' + r.doc_id : '/bills/' + r.doc_id} className="text-emerald hover:underline">{r.number}</Link></td>
                  <td className="text-text-dim">{formatDate(r.issue_date)}</td>
                  <td className="text-text-dim">{formatDate(r.due_date)}</td>
                  <td className={'num ' + (r.days_overdue > 30 ? 'text-rust' : r.days_overdue > 0 ? 'text-gold' : 'text-emerald')}>{r.days_overdue > 0 ? r.days_overdue + 'd' : 'current'}</td>
                  <td><span className={'pill ' + (r.bucket === 'current' ? 'pill-paid' : r.bucket === 'd30' || r.bucket === 'd60' ? 'pill-sent' : 'pill-overdue')}>{BUCKET_LABEL[r.bucket]}</span></td>
                  <td className="text-right num pr-5 font-semibold">{formatMoney(r.outstanding, r.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
